import { useState, type FormEvent } from 'react';
import { Link } from '../../../components/Link';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import { Icon } from '../../../components/icons';
import { Button, Card, Field, Input, cn } from '../../../components/ui';
import { DeferredTurnstileWidget } from '../../../components/TurnstileWidget';
import { useTurnstileGate } from '../../../lib/useAbuseConfig';
import { ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

interface SpamScoreResult {
  score: number;
  verdict: 'clean' | 'risky' | 'spammy';
  ruleHits: Array<{ id: string; label: string; points: number; detail: string }>;
  summary: string;
}

const VERDICT_TONE: Record<SpamScoreResult['verdict'], string> = {
  clean: 'text-allow',
  risky: 'text-verify',
  spammy: 'text-review',
};

/**
 * Free email content spam-score checker. Runs locally against the SpamAssassin-style
 * rule engine — no ELV / provider credits consumed regardless of how many anonymous
 * users hit it. Same /public/tools/spamscore endpoint the API exposes. The form +
 * result UI are injected into ToolPageLayout's `body`; the layout owns SEO + JSON-LD.
 */
export function SpamCheckerPage() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fromDomain, setFromDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const gate = useTurnstileGate();

  const check = useMutation<SpamScoreResult, ApiError>({
    mutationFn: () =>
      api<SpamScoreResult>('/public/tools/spamscore', {
        method: 'POST',
        body: { subject: subject || undefined, body, fromDomain: fromDomain || undefined },
        auth: false,
        headers: gate.headers,
      }),
    onSuccess: () => {
      setError(null);
      setRateLimited(false);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 429) {
        setRateLimited(true);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : 'Lookup failed.');
        setRateLimited(false);
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    check.mutate();
  }

  const content: ToolPageContent = {
    slug: 'email-spam-checker',
    title: 'Free email spam checker',
    appName: 'Email spam checker',
    tagline: 'Score your subject + body against ~15 deliverability rules before you send.',
    eyebrow: 'Free tool · No signup · Runs locally',
    description:
      'Free email spam-score checker. Score your subject + body against ~15 deliverability rules; see exactly which rules are flagging your email.',
    icon: <Icon.Mail />,
    accent: 'verify',
    intro:
      'Paste your subject and body — we score the content against ~15 deliverability rules (urgency language, link density, ALL CAPS, trigger words, image-only emails, …) and show exactly which ones to fix. Runs locally, no provider calls.',
    body: (
      <>
        <Card className="p-6">
          <form onSubmit={onSubmit} {...gate.armProps} className="flex flex-col gap-3">
            <Field label="Subject (optional)">
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Your latest update"
                maxLength={200}
              />
            </Field>
            <Field label="Sender domain (optional)" hint="Lets us cross-check SPF / DKIM / DMARC posture.">
              <Input
                value={fromDomain}
                onChange={(e) => setFromDomain(e.target.value.toLowerCase())}
                placeholder="example.com"
                maxLength={255}
              />
            </Field>
            <Field label="Email body" hint="Plain text or HTML.">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Hi Sarah,&#10;&#10;Following up on…"
                rows={10}
                className="block w-full rounded-[10px] border border-border bg-bg p-3 font-mono text-[12.5px] text-txt outline-none transition placeholder:text-faint focus:border-accentdeep"
              />
            </Field>
            <DeferredTurnstileWidget gate={gate} />
            <Button
              type="submit"
              variant="primary"
              disabled={check.isPending || !body.trim() || !gate.ready}
              className="self-start"
            >
              {check.isPending ? 'Scoring…' : 'Check spam score'}
            </Button>
            {error && <p className="text-[13px] text-review">{error}</p>}
            {rateLimited && (
              <p className="text-[13px] text-verify">
                You've hit the free daily limit (8 / IP). <Link to="/register" className="text-accent hover:underline">Create a workspace</Link> for unlimited checks.
              </p>
            )}
          </form>
        </Card>

        {check.data && (
          <div className="mt-6">
            <Card className="p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-faint">Spam score</div>
                  <div className={cn('mt-1 font-mono text-[11px] uppercase tracking-wider', VERDICT_TONE[check.data.verdict])}>
                    {check.data.verdict}
                  </div>
                </div>
                <div className={cn('font-display text-5xl font-extrabold tabular-nums', VERDICT_TONE[check.data.verdict])}>
                  {check.data.score}
                  <span className="ml-1 text-base font-normal text-faint">/100</span>
                </div>
              </div>
              <p className="mt-3 text-[13.5px] text-muted">{check.data.summary}</p>
            </Card>

            {check.data.ruleHits.length > 0 && (
              <Card className="mt-4 overflow-hidden">
                <div className="border-b border-bordersoft px-4 py-3">
                  <h3 className="text-sm font-semibold">Rules that fired</h3>
                  <p className="mt-0.5 text-[12px] text-faint">Highest-points rules at the top.</p>
                </div>
                <ul className="divide-y divide-bordersoft">
                  {check.data.ruleHits.map((hit) => (
                    <li key={hit.id} className="px-4 py-3">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="text-sm font-medium">{hit.label}</div>
                        <div className="font-mono text-[11px] text-review tabular-nums">+{hit.points} pts</div>
                      </div>
                      <p className="mt-1 text-[12.5px] text-muted">{hit.detail}</p>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        )}
      </>
    ),
    educational: {
      title: 'What a content spam check catches — and what it can\'t',
      paragraphs: [
        'Mailbox providers and spam filters look at far more than your sending reputation; the words, structure, and markup of the message itself feed a content score. Filters like SpamAssassin assign points for patterns that correlate with spam — urgency phrases ("act now", "limited time"), money and prize language, excessive ALL CAPS or exclamation marks, a high ratio of links to text, image-only emails with no plain-text alternative, and broken or mismatched HTML. Cross the threshold and your message gets downranked into spam or quarantined, regardless of how clean your domain is.',
        'This checker runs your subject and body through ~15 of those rules and returns a 0-100 score plus a verdict (clean, risky, spammy) and an itemised list of exactly which rules fired and how many points each cost. That itemisation is the point: instead of a vague "your email looks spammy", you get a concrete punch list — drop the all-caps subject, add a text part, trim the link density — so you can fix the specific issues before you send.',
        'Content scoring belongs to the pre-send QA stage of the email lifecycle, after your domain authentication is set up but before you hit send. It\'s the fast, free first pass. What it can\'t tell you is where a real message actually lands — that depends on your reputation, list hygiene, engagement history, and each provider\'s individual filters. For the authoritative answer you need a seed-list inbox placement test that sends to real Gmail, Yahoo, and Outlook addresses and reports inbox vs spam vs missing per provider.',
      ],
    },
    faq: [
      { q: 'Is a low score a guarantee my email reaches the inbox?', a: 'No. A clean content score removes one common reason mail gets filtered, but placement also depends on your domain reputation, list hygiene, recipient engagement, and each provider\'s own filters. Content scoring is a necessary pre-send check, not the whole picture — for the definitive answer, run a real inbox placement test.' },
      { q: 'What rules does it check?', a: 'About 15 SpamAssassin-style content rules: urgency and money language, trigger words, excessive capitalisation and punctuation, link density, image-to-text ratio, image-only emails, missing plain-text part, and common HTML problems. Each rule that fires is listed with its point cost.' },
      { q: 'Why do I need to add a sender domain?', a: 'It\'s optional, but supplying it lets the checker cross-reference your SPF / DKIM / DMARC posture against the content score, since authentication problems and content problems compound. Leave it blank to score content only.' },
      { q: 'Does this send my email anywhere?', a: 'No. The check runs against a local rule engine — your subject and body are scored on the server and never delivered to any mailbox or third-party provider. It consumes no sending reputation.' },
      { q: 'How do I improve a high (spammy) score?', a: 'Work the itemised list top-down — the highest-point rules first. Common quick wins: remove all-caps from the subject, cut urgency and prize language, lower the number of links relative to text, and always include a plain-text part alongside HTML.' },
      { q: 'When should I run this?', a: 'As pre-send QA on every campaign, newsletter, and important transactional template — ideally on the final copy right before scheduling. It takes seconds and catches the content mistakes that quietly route good mail to spam.' },
    ],
    relatedTools: [
      { slug: 'spam-words-checker', title: 'Spam words checker', tagline: 'Highlight the exact trigger words and phrases in your copy.' },
      { slug: 'subject-line-tester', title: 'Subject line tester', tagline: 'Score a subject line for length, spam risk, and impact.' },
      { slug: 'domain-checker', title: 'Domain health checker', tagline: 'Composite SPF / DKIM / DMARC / MX score for any domain.' },
    ],
    ctaTitle: 'Want to see *where* your email actually lands?',
    ctaBody:
      "SieveGuard's Inbox Placement add-on sends a real test to seed addresses on Gmail, Yahoo, and Outlook and reports inbox vs spam vs missing per ESP — the authoritative answer no content checker can give you.",
  };

  return <ToolPageLayout content={content} />;
}

// Default export required by React Router framework-mode route modules.
export default SpamCheckerPage;
